<template>
  <div ref="scrollContainer">
    <slot v-for="(item, index) in itemsToDisplay" :index="index" :item="item"/>
  </div>
</template>

<script lang="ts">
import Component from 'vue-class-component'
import BasicComponent from "@/global/BasicComponent";
import {Prop, Watch} from "vue-property-decorator";

/**
 * This component can be used to enable lazy loading of many items as soon as the user scrolls them into the viewport
 */
@Component({
  components: {},
  inheritAttrs: true
})
export default class VirtualScroll extends BasicComponent {

  @Prop(Boolean) active: boolean;
  /**
   * Number of items to display initially
   */
  @Prop(Number) initialItemsCount: number;
  /**
   * Added to the viewport height so that elements below the viewport can be considered to be in the viewport.
   */
  @Prop(Number) preloadPadding: number;
  @Prop(Array) items: Array<any>;
  /**
   * The container which is being scrolled
   */
  @Prop([Window, Element]) visibleWindow: Window | Element;

  displayItemsCount: number;
  itemsToDisplay: any[] = [];
  lastItemWhenRequestedNewItems: any = null;

  get hasMoreItems(): boolean {
    return this.itemsToDisplay.length < this.items.length;
  }

  created() {
    this.displayItemsCount = this.initialItemsCount || 1;
    this.onVisibleWindowChange(this.visibleWindow, undefined);
  }

  mounted() {
    //if (this.$store.getters.step === "2") this.trackPage("Bild-Präsentation")
    this.displayAdditionalItemsIfNecessary();
  }

  resetItemsToDisplay(): void {
    this.displayItemsCount = this.initialItemsCount;
    this.itemsToDisplay = [];
    this.lastItemWhenRequestedNewItems = null;
    this.displayAdditionalItemsIfNecessary();
  }

  displayAdditionalItemsIfNecessary() {
    if (!this.active) return;
    //console.trace();
    if (!this.hasMoreItems) {
      return;
    }
    this.$nextTick(() => {
      const lastItem = this.getLastItem();
      if (!lastItem && this.itemsToDisplay.length > 0) { // Container not active anymore
        //console.log("Returning because no last item found");
        return;
      }
      if (lastItem && this.isElementBelowViewport(lastItem)) {
        //console.log("Returning because not below viewport");
        return;
      }
      if (lastItem && this.isElementBelowViewport(lastItem) == null) { // Can't be calculated because parent has height=0
        //console.log("Returning because parent has height 0");
        return;
      }
      if (lastItem && lastItem === this.lastItemWhenRequestedNewItems) {
        //console.log("Returning because already requested for this last item");
        return; // Already requested images for this last image index
      }
      //if (this.displayItemsCount > 50) return;

      this.lastItemWhenRequestedNewItems = lastItem;
      this.displayItemsCount++;
      //console.log("Now displaying " + this.displayItemsCount + " items");
      for (let i = this.itemsToDisplay.length; i < Math.min(this.displayItemsCount, this.items.length); i++) {
        this.itemsToDisplay.push(this.items[i]);
      }
      this.displayAdditionalItemsIfNecessary();
    });
  }

  isElementBelowViewport(el: HTMLElement): boolean {
    let parent = this.visibleWindow;
    //console.log("Parent", parent);
    let parentHeight = parent instanceof Window ? parent.innerHeight : parent.clientHeight;
    if (parentHeight === 0 && parent instanceof HTMLElement) { // Transition may be running
      return null;
    }
    //console.log("Only parent height is " + parentHeight);
    const y = parent instanceof Window ? el.getBoundingClientRect().top : (el.offsetTop - parent.scrollTop);
    //console.log("Element at " + y + " parent height: " + (parentHeight + this.preloadPadding));
    return y > parentHeight + this.preloadPadding;
  }

  getLastItem(): HTMLElement {
    const container = this.$refs.scrollContainer as Element;
    if (!container) return undefined;
    return container.lastElementChild as HTMLElement;
  }

  deregisterListeners(): void {

  }

  @Watch("active")
  onActiveChange(newVal, oldVal) {
    if (newVal) {
      this.visibleWindow.addEventListener('scroll', this.displayAdditionalItemsIfNecessary);
      this.resetItemsToDisplay();
    } else {
      this.visibleWindow.removeEventListener('scroll', this.displayAdditionalItemsIfNecessary);
    }
  }

  @Watch("visibleWindow")
  onVisibleWindowChange(newVal, oldVal) {
    if (oldVal) {
      oldVal.removeEventListener('scroll', this.displayAdditionalItemsIfNecessary);
    }
    if (newVal) {
      newVal.addEventListener('scroll', this.displayAdditionalItemsIfNecessary);
      this.displayAdditionalItemsIfNecessary();
    }
  }

  @Watch("items")
  onItemsChange(newVal: Array<any>, oldVal: Array<any>) {
    if (oldVal !== newVal && oldVal) {
      if (newVal.length !== oldVal.length || newVal.some((val, index) => oldVal[index] != val)) {
        this.resetItemsToDisplay();
      }
    }
  }

}

</script>
<style lang="scss" scoped>

</style>
<style lang="scss">

</style>